import { useState } from "react";
import { http, apiError } from "@/lib/api";
import { trackInitiateCheckout } from "@/lib/metaPixel";
import { Play, Lightning, Check, ShieldCheck, Timer, ArrowRight, X, Warning } from "@phosphor-icons/react";

/**
 * /join — paid-ads VSL funnel landing.
 *
 * Emotionally-loaded direct-response structure aimed at men 30+:
 *   1. Hero + hard pattern interrupt (mirror moment)
 *   2. Pain agitation (specific, uncomfortable, honest)
 *   3. Before/after — Jimmy's own transformation as proof
 *   4. VSL
 *   5. What you get (benefits)
 *   6. Client social proof
 *   7. FAQ
 *   8. Urgency-driven final CTA
 *
 * Signup posts to /api/funnel/start-trial → creates user + 7-day-trial Stripe
 * subscription → redirect to Stripe checkout.
 */

// VSL — YouTube Shorts embed (portrait 9:16). Params:
//   rel=0            no related-video suggestions at end
//   modestbranding=1 minimal YouTube branding overlay
//   playsinline=1    plays inline on iPhone instead of forcing fullscreen
const VSL_EMBED_URL = "https://www.youtube.com/embed/dit6oR7ZNn0?rel=0&modestbranding=1&playsinline=1";
// If true, renders the video slot at 9:16 (portrait — for YouTube Shorts / vertical content).
// If false, defaults to 16:9 (traditional landscape VSL).
const VSL_IS_PORTRAIT = true;

// Jimmy's before/after photos — already sitting in /public from the main landing.
const JIMMY_BEFORE = "/jimmy-before.jpg";
const JIMMY_AFTER = "/jimmy-after.png";

const PAIN_POINTS = [
  "You wake up tired. Not because you didn't sleep — because you know you promised yourself Monday was the day. Again.",
  "You can see your gut in every T-shirt. It rolls over your belt when you sit down. You've started standing for photos.",
  "Your knees hurt on the stairs. You're 38, not 68, and it scares you.",
  "You've bought every course, every plan, every shake. They all worked for 3 weeks.",
  "The people closest to you stopped commenting on your body a while ago. That silence is louder than anything they could say.",
  "You look in the mirror and don't recognise the guy staring back.",
];

const BENEFITS = [
  { icon: Check, title: "The 7 Lazy Lean Habits™", body: "No calorie counting. No weighing food. No gym-bro macros. Just 7 daily habits that get you leaner every week. Simple enough to do them without thinking." },
  { icon: Check, title: "Custom 12-week plan built for YOU", body: "Not a PDF. Real workouts + meals personalised to your body, your schedule, your equipment. Built by me. Delivered in your dashboard within 24 hours." },
  { icon: Check, title: "Direct chat with Coach Jimmy", body: "Not an AI bot. Not a call centre. You message me — I reply. Same day. That's the accountability that actually holds." },
  { icon: Check, title: "Track everything in one app", body: "Weight, lifts, meals, habits, before/after photos. All in your pocket. Simple enough for anyone to work." },
];

const FAQS = [
  { q: "I've tried everything. Why would this work?", a: "Because it's not another diet. Diets fail because they require willpower you don't have at 9pm after a 12-hour day. Habits don't. They stack. Week 1 you do one. By Week 12 they're automatic — and you're 8-12kg lighter without noticing you tried." },
  { q: "How much time does it take?", a: "30-45 minutes, 3-4x a week. The habits fit into your existing life — a walk on your lunch break, protein at breakfast, water on your desk. No 6am boot-camps, no meal-prep Sundays." },
  { q: "I'm 40+ and my body doesn't respond like it used to.", a: "That's exactly who this is built for. Your metabolism isn't broken — it's just running the wrong program. Guys 40+ are my highest-success bracket. See the testimonials above." },
  { q: "What if it doesn't work for me?", a: "For $7 you get full 7-day access. If Lazy Lean isn't a fit, cancel from your dashboard before day 7 — the $7 is all you pay, no further charges. If it does work, $89/wk kicks in on day 8. Cancel any week from your dashboard." },
  { q: "What am I charged today?", a: "$7 today for full access to the app, the plan, the workouts, the coach chat — everything. On day 8, $89/wk auto-bills to the same card. Cancel any time from your dashboard." },
];

export default function JoinPage() {
  const [showSignup, setShowSignup] = useState(false);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Sticky top strip */}
      <div className="border-b border-[#262626] py-4 px-6 flex items-center justify-between">
        <div className="font-oswald text-2xl uppercase tracking-wider">
          Lazy<span className="text-[#FF3333]">Lean</span>
        </div>
        <a href="/login" className="text-zinc-500 hover:text-white text-xs font-manrope uppercase tracking-[0.15em]" data-testid="funnel-login-link">
          Already a member? Log in
        </a>
      </div>

      {/* HERO — urgency-loaded pattern interrupt */}
      <section className="pt-14 pb-10 md:pt-20 md:pb-16 px-6 border-b border-[#262626] relative overflow-hidden" data-testid="funnel-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF3333]/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 border border-[#FF3333]/40 bg-[#FF3333]/10 px-3 py-1.5 mb-6" data-testid="funnel-hero-eyebrow">
            <Warning size={12} weight="fill" className="text-[#FF3333]" />
            <span className="text-[10px] font-manrope uppercase tracking-[0.2em] text-[#FF3333]">For men 30+ · Stop waiting for Monday</span>
          </div>
          <h1 className="font-oswald text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[0.95] mb-6">
            You didn&apos;t plan<br/>to become <span className="text-[#FF3333]">this guy.</span>
          </h1>
          <p className="text-zinc-300 font-manrope text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            The tired guy in the mirror. The one avoiding photos. The one who says <em>&ldquo;this Monday&rdquo;</em> every Sunday for 5 years running.
          </p>
          <p className="text-white font-manrope text-lg md:text-2xl max-w-2xl mx-auto leading-relaxed mt-4 font-semibold">
            You&apos;re not lazy. You&apos;re not broken. You&apos;re a man who&apos;s never been given a system built for how you actually live.
          </p>

          {/* Urgency block */}
          <div className="mt-8 max-w-2xl mx-auto border-y border-[#FF3333]/30 py-6 px-4 bg-[#FF3333]/5" data-testid="funnel-hero-urgency">
            <p className="text-[#FF3333] font-oswald uppercase tracking-wide text-xl md:text-3xl leading-tight">
              Not next Monday.<br/>Not the 1st of the month.<br/>Not &ldquo;when things slow down.&rdquo;
            </p>
            <p className="text-white font-oswald uppercase tracking-wide text-2xl md:text-4xl mt-4">
              <span className="text-[#FF3333]">Right. Now.</span>
            </p>
          </div>

          {/* VSL — moved up into the hero (was previously a standalone section
              further down the page). Sits between the urgency block and the
              primary CTA so cold-ad traffic can watch → click without ever
              scrolling. */}
          <div className="mt-10" data-testid="funnel-hero-vsl">
            <div className="id-label text-[#FF3333] mb-3">— WATCH THIS FIRST · 60 SECONDS —</div>
            <h2 className="font-oswald text-2xl md:text-3xl font-bold uppercase tracking-tight mb-6">
              The exact system, <span className="text-[#FF3333]">in 60 seconds.</span>
            </h2>
            <div
              className={
                "relative border border-[#FF3333]/40 bg-[#141414] overflow-hidden group mx-auto " +
                (VSL_IS_PORTRAIT
                  ? "aspect-[9/16] max-w-[360px]"
                  : "aspect-video max-w-full")
              }
              data-testid="vsl-video-slot"
            >
              {/* Masks YouTube's auto-generated Shorts channel-handle pill in the
                  top-left. YouTube's CDN caches these thumbnails aggressively
                  and different geo-edges update at different times, so even
                  when the channel is renamed to "Lazy Lean Method" some edges
                  still serve the old "TRICKDMX" pill. Rather than gamble with
                  ad traffic, we permanently mask it. pointer-events:none so
                  it never intercepts the play tap. */}
              {VSL_EMBED_URL && VSL_IS_PORTRAIT && (
                <div
                  aria-hidden="true"
                  className="absolute top-3 left-3 h-9 w-40 bg-black pointer-events-none z-10 rounded"
                  data-testid="vsl-branding-mask"
                />
              )}
              {VSL_EMBED_URL ? (
                <iframe
                  className="w-full h-full"
                  src={VSL_EMBED_URL}
                  title="Lazy Lean Method — VSL"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4 cursor-pointer group-hover:bg-[#181818] transition-colors">
                  <div className="w-20 h-20 md:w-24 md:h-24 bg-[#FF3333] flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Play size={40} weight="fill" className="text-white ml-1" />
                  </div>
                  <div className="text-zinc-400 font-manrope text-sm uppercase tracking-[0.2em]">Play VSL</div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-10 flex flex-col items-center gap-4">
            <button
              onClick={() => setShowSignup(true)}
              className="id-btn-primary px-10 py-5 text-base md:text-lg animate-pulse-cta"
              data-testid="funnel-cta-hero"
            >
              <Lightning size={20} weight="fill" /> Let&apos;s Go — Start My $7 Trial <ArrowRight size={18} weight="bold" />
            </button>
            <div className="text-zinc-400 font-manrope italic text-sm max-w-md">
              Don&apos;t think. Just go. That&apos;s literally the whole method.
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-[10px] text-zinc-500 font-manrope uppercase tracking-[0.2em]">
              <span className="flex items-center gap-2"><ShieldCheck size={12} weight="fill" className="text-[#FF3333]" /> Just $7 for 7 days</span>
              <span className="flex items-center gap-2"><Timer size={12} weight="fill" className="text-[#FF3333]" /> Cancel any time</span>
              <span className="flex items-center gap-2"><Check size={12} weight="bold" className="text-[#FF3333]" /> Instant access</span>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN AGITATION */}
      <section className="py-16 md:py-24 px-6 border-b border-[#262626] bg-[#0F0F0F]" data-testid="funnel-pain">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="id-label text-[#FF3333] mb-3">— IF THIS SOUNDS LIKE YOU —</div>
            <h2 className="font-oswald text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1.05]">
              You don&apos;t need another diet.<br/>You need someone who <span className="text-[#FF3333]">gets it.</span>
            </h2>
          </div>
          <div className="space-y-4">
            {PAIN_POINTS.map((p, i) => (
              <div key={i} className="border-l-2 border-[#FF3333] bg-[#141414] px-5 py-4 font-manrope text-zinc-300 text-base md:text-lg leading-relaxed" data-testid={`funnel-pain-${i}`}>
                {p}
              </div>
            ))}
          </div>
          <p className="text-center text-zinc-400 font-manrope italic mt-10 max-w-2xl mx-auto">
            I know all of this because I lived it. I blew out, lost every ounce of motivation, watched my gut push against every T-shirt I owned, felt it fold over my waistband every time I sat down. Out of breath just walking up a flight of stairs.
          </p>
        </div>
      </section>

      {/* BEFORE / AFTER PROOF — Jimmy */}
      <section className="py-16 md:py-24 px-6 border-b border-[#262626]" data-testid="funnel-before-after">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="id-label text-[#FF3333] mb-3">— MY OWN TRANSFORMATION —</div>
            <h2 className="font-oswald text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1.05]">
              I was the guy on the left.<br/>Now I&apos;m building the system on the right.
            </h2>
            <p className="text-zinc-400 font-manrope mt-5 max-w-2xl mx-auto">
              Two years ago I&apos;d blown out — motivation gone, gut pushing against every T-shirt, folding over my belt when I sat down. Not because I was lazy — because every plan I tried was built for someone I wasn&apos;t.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
            {/* BEFORE */}
            <div className="relative aspect-[4/5] overflow-hidden border border-[#262626] bg-[#141414]" data-testid="jimmy-before">
              <img src={JIMMY_BEFORE} alt="Coach Jimmy — before" className="absolute inset-0 w-full h-full object-cover object-top grayscale" />
              <div className="absolute top-4 left-4 bg-[#0A0A0A]/85 border border-zinc-700 px-3 py-1.5 backdrop-blur-sm">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Before</div>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent">
                <div className="font-oswald text-2xl md:text-3xl uppercase tracking-wide text-zinc-300">Blown Out</div>
                <div className="text-zinc-500 text-xs font-manrope mt-1">Barrel gut in every T-shirt.<br/>Motivation? Nowhere.</div>
              </div>
            </div>
            {/* AFTER */}
            <div className="relative aspect-[4/5] overflow-hidden border border-[#FF3333] bg-[#141414]" data-testid="jimmy-after">
              <img src={JIMMY_AFTER} alt="Coach Jimmy — after" className="absolute inset-0 w-full h-full object-cover object-top" />
              <div className="absolute top-4 left-4 bg-[#FF3333] px-3 py-1.5">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">After · 12 Weeks</div>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent">
                <div className="font-oswald text-2xl md:text-3xl uppercase tracking-wide text-white">Locked In</div>
                <div className="text-zinc-300 text-xs font-manrope mt-1">7 habits. Zero calorie counting.<br/>No weighing food. No 5am cardio.</div>
              </div>
            </div>
          </div>

          <p className="text-center text-white font-manrope font-semibold mt-10 max-w-2xl mx-auto text-lg leading-relaxed">
            I didn&apos;t become a monk. I didn&apos;t give up beer with mates. I built a system that fits around <em>a real life</em> — and I&apos;m going to build the same one for you.
          </p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 px-6 border-b border-[#262626]" data-testid="funnel-benefits">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="id-label text-[#FF3333] mb-3">— WHAT YOU GET —</div>
            <h2 className="font-oswald text-3xl md:text-5xl font-bold uppercase tracking-tight">
              Everything to get lean.<br/>Nothing to overthink.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={i} className="border border-[#262626] bg-[#0F0F0F] p-6" data-testid={`funnel-benefit-${i}`}>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-[#FF3333] flex-shrink-0 flex items-center justify-center">
                    <b.icon size={20} weight="bold" className="text-white" />
                  </div>
                  <div>
                    <div className="font-oswald text-xl uppercase tracking-wide mb-2">{b.title}</div>
                    <p className="text-zinc-400 font-manrope text-sm leading-relaxed">{b.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-16 px-6 border-b border-[#262626] bg-[#0F0F0F]" data-testid="funnel-social-proof">
        <div className="max-w-5xl mx-auto text-center">
          <div className="id-label text-[#FF3333] mb-3">— MEN DOING IT NOW —</div>
          <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase tracking-tight mb-8">Real men. Real results. Real lives.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { name: "Mike, 38", quote: "\"Down 8kg in 6 weeks. First time I&apos;ve stuck with anything past Feb. Feels sustainable — that&apos;s the difference.\"" },
              { name: "James, 41", quote: "\"I&apos;m not hiding from beach photos this summer. First time in 10 years. People at work have noticed. That&apos;s all I&apos;ll say.\"" },
              { name: "Ryan, 35", quote: "\"12kg down. Not a diet — it&apos;s just how I eat now. And I still have beers with the lads on Friday.\"" },
            ].map((t, i) => (
              <div key={i} className="border border-[#262626] bg-[#0A0A0A] p-5 text-left" data-testid={`funnel-testimonial-${i}`}>
                <p className="text-zinc-300 font-manrope text-sm leading-relaxed italic" dangerouslySetInnerHTML={{ __html: t.quote }} />
                <div className="id-label text-[#FF3333] mt-4">— {t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 border-b border-[#262626]" data-testid="funnel-faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="id-label text-[#FF3333] mb-3">— QUESTIONS —</div>
            <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase tracking-tight">Before you decide</h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} className="border border-[#262626] bg-[#0F0F0F] p-5 group" data-testid={`funnel-faq-${i}`}>
                <summary className="font-oswald text-lg uppercase tracking-wide cursor-pointer flex items-center justify-between list-none">
                  <span dangerouslySetInnerHTML={{ __html: f.q }} />
                  <span className="text-[#FF3333] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="text-zinc-400 font-manrope text-sm mt-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: f.a }} />
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 px-6 bg-gradient-to-br from-[#FF3333]/15 via-transparent to-transparent border-b border-[#262626]" data-testid="funnel-final-cta">
        <div className="max-w-2xl mx-auto text-center">
          <div className="id-label text-[#FF3333] mb-4">— NO MORE MONDAYS —</div>
          <h2 className="font-oswald text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1.05] mb-6">
            You&apos;ve been putting this off<br/>for <span className="text-[#FF3333]">how long now?</span>
          </h2>
          <p className="text-zinc-300 font-manrope text-base md:text-lg mb-6 leading-relaxed">
            Every day you wait is another day older, another day heavier, another day further from the guy you know you can be.
          </p>
          <p className="text-white font-manrope text-lg md:text-2xl mb-8 font-semibold leading-tight">
            Stop reading.<br/>Stop deciding.<br/><span className="text-[#FF3333]">Start.</span>
          </p>
          <button onClick={() => setShowSignup(true)} className="id-btn-primary px-10 py-5 text-base md:text-lg animate-pulse-cta" data-testid="funnel-cta-footer">
            <Lightning size={20} weight="fill" /> Let&apos;s Go — I&apos;m In <ArrowRight size={18} weight="bold" />
          </button>
          <div className="mt-4 text-zinc-400 font-manrope italic text-sm">
            30 seconds from now, you&apos;re inside the app.
          </div>
          <div className="mt-6 text-[10px] text-zinc-500 font-manrope uppercase tracking-[0.2em]">
            Instant access · Just $7 for 7 days · Cancel any time
          </div>
        </div>
      </section>

      <div className="py-6 px-6 text-center text-xs text-zinc-600 font-manrope">
        © Lazy Lean Method. <a href="/legal/terms" className="hover:text-white">Terms</a> · <a href="/legal/privacy" className="hover:text-white">Privacy</a>
      </div>

      {showSignup && <SignupModal onClose={() => setShowSignup(false)} />}
    </div>
  );
}

function SignupModal({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim() || !email.trim() || password.length < 6) {
      setErr("Fill everything — password 6+ chars.");
      return;
    }
    // Fire Meta Pixel InitiateCheckout — this is the moment the visitor has
    // committed to trying the trial (form fully filled, submit tapped) and
    // is about to be redirected to Stripe. Meta uses this event to optimise
    // ad delivery toward lookalike "checkout starters".
    trackInitiateCheckout();
    setBusy(true);
    try {
      const { data } = await http.post("/funnel/start-trial", {
        name: name.trim(),
        email: email.trim(),
        password,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(apiError(e, "Couldn't start your trial. Try again."));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="funnel-signup-modal">
      <div className="bg-[#0A0A0A] border border-[#FF3333]/40 w-full max-w-md my-8 shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#262626] px-6 py-4">
          <div>
            <div className="id-label text-[#FF3333] mb-1">— $7 · 7-DAY TRIAL —</div>
            <h2 className="font-oswald text-2xl uppercase tracking-wide">Get instant access</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="text-zinc-500 hover:text-white disabled:opacity-40" data-testid="funnel-signup-close">
            <X size={22} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <p className="text-zinc-400 text-xs font-manrope leading-relaxed">
            $7 today for full 7-day access. On day 8, $89/wk auto-bills (cancel anytime from your dashboard).
          </p>

          <div>
            <label className="id-label block mb-1.5">First name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus className="id-input" data-testid="funnel-signup-name" placeholder="James" />
          </div>
          <div>
            <label className="id-label block mb-1.5">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="id-input" data-testid="funnel-signup-email" placeholder="james@example.com" />
          </div>
          <div>
            <label className="id-label block mb-1.5">Choose a password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="id-input" data-testid="funnel-signup-password" placeholder="6+ characters" />
          </div>

          {err && (
            <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333] font-manrope" data-testid="funnel-signup-error">
              {err}
            </div>
          )}

          <button type="submit" disabled={busy} className="id-btn-primary w-full py-4 text-sm disabled:opacity-40" data-testid="funnel-signup-submit">
            {busy ? "Redirecting to secure checkout..." : <>Continue — Start My Trial <ArrowRight size={16} weight="bold" /></>}
          </button>

          <div className="flex items-center justify-center gap-2 text-[10px] text-zinc-500 font-manrope uppercase tracking-[0.15em] pt-2">
            <ShieldCheck size={12} weight="fill" className="text-[#FF3333]" /> Secure payment · Powered by Stripe
          </div>
        </form>
      </div>
    </div>
  );
}
